import { useState, useCallback, useEffect } from "react";
import { SearchResults, Location, BilletGroup } from "../../../../models";

interface SearchCriteria {
  locations: string[];
  heats: string[];
  sections: string[];
  lengths: string[];
  grades: string[];
  qualities: string[];
  destinations: string[];
}

interface UseSearchIntegrationProps {
  locations: Location[]; // All locations for search filtering (regular + special)
  regularLocations: Location[]; // Only regular locations for page calculation
  groupsByLocationId: { [key: number]: BilletGroup[] };
  totalPages: number;
  onPageChange: (page: number) => void;
  onModalClose: () => void;
  searchCriteria: SearchCriteria;
}

interface UseSearchIntegrationReturn {
  searchResults: SearchResults | null;
  isSearchActive: boolean;
  handleSearchResults: (results: SearchResults | null) => void;
  handleClearSearch: () => void;
  isLocationSearchHighlighted: (locationId: number) => boolean;
  getSearchMatchingGroupIds: (
    locationId: number,
    groupsByLocationId: { [key: number]: BilletGroup[] },
  ) => number[];
  matchingPages: number[];
}

export const useSearchIntegration = ({
  locations,
  regularLocations,
  groupsByLocationId,
  totalPages,
  onPageChange,
  onModalClose,
  searchCriteria,
}: UseSearchIntegrationProps): UseSearchIntegrationReturn => {
  const [searchResults, setSearchResults] = useState<SearchResults | null>(
    null,
  );
  const [isSearchActive, setIsSearchActive] = useState(false);

  /**
   * Check if search criteria has any active filters
   */
  const hasActiveSearchCriteria = useCallback(() => {
    return Object.values(searchCriteria).some(
      (criteriaArray) => criteriaArray.length > 0,
    );
  }, [searchCriteria]);

  /**
   * Check if a value is a number (for HeatID vs HeatNumber detection)
   */
  const isNumericId = (value: string): boolean => {
    return /^\d+$/.test(value);
  };

  /**
   * Apply search filters to locations and groups data (moved from SearchModal)
   */
  const applySearchFilters = useCallback(
    (
      locations: Location[],
      groupsByLocationId: { [key: number]: BilletGroup[] },
      criteria: SearchCriteria,
    ) => {
      const matchingLocationIds: number[] = [];
      const matchingGroupIds: number[] = [];

      // Apply search filters to locations and groups data

      locations.forEach((location) => {
        const groups = groupsByLocationId[location.LocationID] || [];
        let locationHasMatches = false;

        groups.forEach((group) => {
          let groupMatches = true;

          // Check location filter - handle both string and numeric IDs
          if (criteria.locations.length > 0) {
            const locationIdStr = location.LocationID.toString();
            const locationIdNum = location.LocationID;
            const hasLocationMatch = criteria.locations.some(
              (criteriaLocation) =>
                criteriaLocation === locationIdStr ||
                criteriaLocation === locationIdNum.toString() ||
                parseInt(criteriaLocation, 10) === locationIdNum,
            );
            if (!hasLocationMatch) {
              groupMatches = false;
            }
          }

          // Check heat filter - handle both HeatID (numeric) and HeatNumber (string name)
          if (criteria.heats.length > 0) {
            const hasHeatMatch = criteria.heats.some((criteriaHeat) => {
              // If criteria is numeric, match against HeatID
              if (isNumericId(criteriaHeat) && group.HeatID != null) {
                const heatIdStr = group.HeatID.toString();
                return (
                  criteriaHeat === heatIdStr ||
                  criteriaHeat === group.HeatID?.toString() ||
                  parseInt(criteriaHeat, 10) === group.HeatID
                );
              }
              // If criteria is not numeric, match against HeatNumber (heat name)
              else if (group.HeatNumber) {
                return group.HeatNumber.toLowerCase().includes(
                  criteriaHeat.toLowerCase(),
                );
              }
              return false;
            });
            if (!hasHeatMatch) {
              groupMatches = false;
            }
          }

          // Check section filter (dimensions) - handle null/empty values
          if (criteria.sections.length > 0 && group.Section) {
            const hasSectionMatch = criteria.sections.some(
              (criteriaSection) =>
                criteriaSection === group.Section ||
                criteriaSection === group.Section?.toString(),
            );
            if (!hasSectionMatch) {
              groupMatches = false;
            }
          }

          // Check length filter
          if (criteria.lengths.length > 0) {
            const groupLength = group.BilletsLength;
            const matches = criteria.lengths.some((criteriaLength) => {
              const numericCriteria =
                typeof criteriaLength === "string"
                  ? parseInt(criteriaLength, 10)
                  : criteriaLength;
              return numericCriteria === groupLength;
            });

            if (!matches) {
              groupMatches = false;
            }
          }

          // Check grade filter - handle both string and numeric grade IDs
          if (criteria.grades.length > 0 && group.GradeID != null) {
            const hasGradeMatch = criteria.grades.some(
              (criteriaGrade) =>
                criteriaGrade === group.GradeID?.toString() ||
                parseInt(criteriaGrade, 10) === group.GradeID,
            );
            if (!hasGradeMatch) {
              groupMatches = false;
            }
          }

          // Check quality filter - handle null/empty values
          if (criteria.qualities.length > 0 && group.BilletsQualityStatusCode) {
            const hasQualityMatch = criteria.qualities.some(
              (criteriaQuality) =>
                criteriaQuality === group.BilletsQualityStatusCode ||
                criteriaQuality === group.BilletsQualityStatusCode?.toString(),
            );
            if (!hasQualityMatch) {
              groupMatches = false;
            }
          }

          // Check destination filter (isForSale) - BIC/BIR mapping only
          // Customer (BIC prefix) = isForSale true, Roanoke (BIR prefix) = isForSale false
          if (criteria.destinations.length > 0) {
            const isForSale = group.isForSale;

            const matchesDestination = criteria.destinations.some((dest) => {
              // Customer: isForSale === true OR "TRUE" (generates BIC group names)
              if (dest === "Customer") {
                return (
                  isForSale === true ||
                  String(isForSale).toUpperCase() === "TRUE"
                );
              }
              // Roanoke: isForSale === false OR "FALSE" (generates BIR group names)
              if (dest === "Roanoke") {
                return (
                  isForSale === false ||
                  String(isForSale).toUpperCase() === "FALSE"
                );
              }
              return false;
            });

            if (!matchesDestination) {
              groupMatches = false;
            }
          }

          if (groupMatches) {
            matchingGroupIds.push(group.BilletsGroupID);
            locationHasMatches = true;
          }
        });

        if (locationHasMatches) {
          matchingLocationIds.push(location.LocationID);
        }
      });

      return {
        locationIds: matchingLocationIds,
        groupIds: matchingGroupIds,
      };
    },
    [],
  );

  /**
   * Calculate which pages contain matching regular locations (special locations are always visible)
   */
  const getMatchingPages = useCallback(
    (matchingLocationIds: number[]) => {
      if (matchingLocationIds.length === 0) return [];

      const matchingPages: number[] = [];

      // Only consider regular locations for page calculation
      // Special locations are always visible on page 1 and don't affect pagination
      regularLocations.forEach((location, index) => {
        if (matchingLocationIds.includes(location.LocationID)) {
          // Calculate page number for this regular location (1-based)
          const pageNumber = Math.floor(index / 7) + 1;
          if (!matchingPages.includes(pageNumber)) {
            matchingPages.push(pageNumber);
          }
        }
      });

      return matchingPages.sort((a, b) => a - b);
    },
    [regularLocations],
  );

  /**
   * Track matching pages for status display
   */
  const [matchingPages, setMatchingPages] = useState<number[]>([]);

  /**
   * Automatically trigger search when criteria changes
   */
  useEffect(() => {
    if (
      locations.length === 0 ||
      Object.keys(groupsByLocationId).length === 0
    ) {
      return; // Wait for data to be loaded
    }

    if (hasActiveSearchCriteria()) {
      // Apply search filters automatically
      const matchingResults = applySearchFilters(
        locations,
        groupsByLocationId,
        searchCriteria,
      );

      const results: SearchResults = {
        locationIds: matchingResults.locationIds,
        groupIds: matchingResults.groupIds,
        searchCriteria: searchCriteria,
      };

      setSearchResults(results);
      setIsSearchActive(true);
    } else {
      // No active criteria - clear search
      setSearchResults(null);
      setIsSearchActive(false);
    }
  }, [searchCriteria, locations, groupsByLocationId]);

  /**
   * Update matching pages when search results change
   */
  useEffect(() => {
    if (searchResults && searchResults.locationIds.length > 0) {
      const pages = getMatchingPages(searchResults.locationIds);
      setMatchingPages(pages);
    } else {
      setMatchingPages([]);
    }
  }, [searchResults, getMatchingPages]);

  /**
   * Handle search results from modal - this will be called when modal saves criteria
   * The actual search is triggered automatically by the useEffect above
   */
  const handleSearchResults = useCallback(
    (results: SearchResults | null) => {
      // Modal is notifying us that search was explicitly triggered
      // Just close modal - no auto-navigation to prevent re-renders
      onModalClose();
    },
    [onModalClose],
  );

  /**
   * Clear search results and highlighting by clearing the search criteria
   */
  const handleClearSearch = useCallback(() => {
    // This will be handled by the parent component by clearing searchCriteria
    // The useEffect will automatically clear results when criteria is empty
  }, []);

  /**
   * Check if a location should be highlighted based on search results
   */
  const isLocationSearchHighlighted = useCallback(
    (locationId: number): boolean => {
      return searchResults?.locationIds.includes(locationId) || false;
    },
    [searchResults],
  );

  /**
   * Get the group IDs that should be highlighted for a specific location
   */
  const getSearchMatchingGroupIds = useCallback(
    (
      locationId: number,
      groupsByLocationId: { [key: number]: BilletGroup[] },
    ): number[] => {
      if (!searchResults) return [];

      // Get all groups from this location that match search criteria
      const locationGroups = groupsByLocationId[locationId] || [];
      return locationGroups
        .filter((group) =>
          searchResults.groupIds.includes(group.BilletsGroupID),
        )
        .map((group) => group.BilletsGroupID);
    },
    [searchResults],
  );

  return {
    searchResults,
    isSearchActive,
    handleSearchResults,
    handleClearSearch,
    isLocationSearchHighlighted,
    getSearchMatchingGroupIds,
    matchingPages,
  };
};
